/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DATE_TIME_FORMAT } from 'src/constants/CommonConstants';
import { Form, Formik } from 'formik';
import FormikInput from 'src/components/formik/FormikInput';
import FormikSelect from 'src/components/formik/FormikSelect';
import { isProduction } from 'src/config/index';
import { withFormikDevtools } from 'formik-devtools-extension';
import { useDispatch } from 'react-redux';
import LinkComponent from 'src/components/LinkComponent';
import classNames from 'classnames';
import { FileDefaultProps, FileYupSchema } from './CrossSalePropTypes';
import SearchContainer from '../search-container/SearchContainer';
import { crossSaleInsertAction, crossSaleRemoveAction, crossSaleSearchAction } from './actions/CrossSaleActions';
import CompanyDropDown from 'src/business-components/CompanyDropDown';
import ProductDropDown from 'src/business-components/ProductDropDown';
import { PRODUCT_CATEGORY_TYPES, PRODUCT_CATEGORY_TYPES_LIST } from 'src/containers/product-container/ProductConstants';
import CatalogueVehicleInsuranceDropDown from 'src/business-components/CatalogueVehicleInsuranceDropDown';

const columns = {
    company: {
        display: 'Company',
        path: 'companyId.companyDisplayName',
    },
    product: {
        display: 'Product',
        path: 'productId.productName',
    },
    catalogue: {
        display: 'Catalogue',
        path: 'catalogueId.catalogueName',
    },
    amount: {
        display: 'Cross Sale Amount',
    },
    productCategory: {
        display: 'productCategory',
    },
    ts: {
        display: 'Ts',
        type: Date,
        format: DATE_TIME_FORMAT.TIMEAGO,
    },
};

const RenderDownload = ({ record }) => (
    <LinkComponent
        className={classNames({
            'd-none': Boolean(record.deleted),
        })}
        onClick={() => {
            window.open(record.url, '_blank');
        }}
    >
        <i className="fas fa-download" />
    </LinkComponent>
);

RenderDownload.propTypes = {
    record: PropTypes.shape({
        deleted: PropTypes.bool,
    }).isRequired,
};

const Render = (props) => {
    // DEV TOOL
    if (!isProduction()) {
        withFormikDevtools(props);
    }

    const {
        values,
    } = props;

    return (
        <Form autoComplete="disabled">
            <div className="form-row">
                <div className="form-group col-md-2">
                    <label>Company</label>
                    <CompanyDropDown
                        value={values.companyId}
                        name="companyId"
                    />
                </div>
                <div className="form-group col-md-2">
                    <label>Category</label>
                    <FormikSelect
                        items={PRODUCT_CATEGORY_TYPES_LIST}
                        name="productCategory"
                        className="form-control"
                    />
                </div>
                <div className="form-group col-md-3">
                    <label>Product</label>
                    <ProductDropDown
                        name="productId"
                        value={values.productId}
                        productCategory={values.productCategory}
                    />
                </div>
                <div className="form-group col-md-3">
                    <label>Catalogue</label>
                    <CatalogueVehicleInsuranceDropDown
                        name="catalogueId"
                        value={values.catalogueId}
                        companyId={values.companyId}
                        productId={values.productId}
                        productCategory={values.productCategory}
                    />
                </div>
                <div className="form-group col-md-2">
                    <label>Cross Sale Amount</label>
                    <div className="custom-file">
                        <FormikInput
                            name="amount"
                            type="text"
                            className="form-control"
                            value="0"
                        />
                    </div>
                </div>
            </div>
            <div className="text-center">
                <button
                    type="submit"
                    className="btn btn-primary"
                >
                    Add
                </button>
            </div>

        </Form>
    );
};

Render.propTypes = {
    values: PropTypes.shape({
        file: PropTypes.any,
    }).isRequired,

};

const CrossSaleContainer = ({
    className,
    title,
    refId,
    is_carLoan,
    source,
}) => {
    // REDUX
    const dispatch = useDispatch();

    const onSubmit = useCallback((
        {
            companyId,
            productCategory,
            productId,
            catalogueId,
            amount,
        },
        { resetForm },
    ) => {
        if(catalogueId==="") catalogueId=null
        dispatch(crossSaleInsertAction({
            refId,
            source,
            companyId,
            productCategory,
            productId,
            catalogueId,
            amount,
        }));
        // resetForm();
    }, [
        refId,
        source,
        dispatch,
    ]);

    const onRemove = useCallback((
        { _id },
        // { resetForm },
    ) => {
        dispatch(crossSaleRemoveAction({
            crossSaleId: _id,
            refId,
            source,
        }));
    }, [
        refId,
        source,
        dispatch,
    ]);

    const where = useMemo(() => ({
        refId,
        source,
    }), [
        refId,
        source,
    ]);
    const dataPath = useMemo(() => `${source}-${refId}.search`, [
        refId,
        source,
    ]);

    if (!is_carLoan || !source) {
        return null;
    }

    return (
        <div className={className}>
            <div className="card mt-3">
                <div className="card-body">
                    <h5 className="card-title">
                        <i className="fas fa-file text-info" />
                        {' '}
                        {title}
                    </h5>
                    <Formik
                        onSubmit={onSubmit}
                        enableReinitialize
                        initialValues={FileDefaultProps}
                        validationSchema={FileYupSchema}
                    >
                        {(formikProps) => (
                            <Render
                                {...formikProps}
                            />
                        )}
                    </Formik>
                    <SearchContainer
                        fullTextSearch={false}
                        className="mt-3"
                        reducer="CrossSaleReducer"
                        dataPath={dataPath}
                        action={crossSaleSearchAction}
                        columns={columns}
                        queryParamsAsWhere={false}
                        where={where}
                        hasRemove
                        removeAction={onRemove}
                        rowDeletedCheckPath="deleted"
                    />
                </div>
            </div>
        </div>
    );
};

CrossSaleContainer.propTypes = {
    className: PropTypes.string,
    title: PropTypes.string,
    refId: PropTypes.string,
    source: PropTypes.string,
};

CrossSaleContainer.defaultProps = {
    title: 'Cross Sales Details',
    className: '',
    refId: null,
    source: null,
};

export default CrossSaleContainer;
